import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { RegisterCta } from "./RegistrationProvider";

export const EASE = [0.2, 0.7, 0.1, 1];

/** Fades + lifts children in once, when they enter the viewport. */
export function Reveal({ as = "div", delay = 0, y = 24, className, children, ...rest }) {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -12% 0px" }}
      transition={{ duration: 0.7, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </M>
  );
}

export function SectionHead({ label, title, lead, split = false, id }) {
  return (
    <div className={`ys-head${split ? " ys-head--split" : ""}`}>
      <div>
        {label && <Reveal as="p" className="ys-label">{label}</Reveal>}
        <Reveal as="h2" className="ys-h2" id={id} delay={0.05} style={{ marginTop: label ? 18 : 0 }}>
          {title}
        </Reveal>
      </div>
      {lead && <Reveal as="p" className="ys-lead" delay={0.12}>{lead}</Reveal>}
    </div>
  );
}

export function Arrow({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 8h11M9 3.5 13.5 8 9 12.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function RegisterLink({ className = "", size, children, onClick }) {
  return (
    <RegisterCta
      className={`ys-btn${size ? ` ys-btn--${size}` : ""} ${className}`}
      onClick={onClick}
    >
      {children || "Register now"} <Arrow />
    </RegisterCta>
  );
}

/** Scrolls to an on-page section, honouring reduced motion. */
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  if (window.history?.replaceState) window.history.replaceState(null, "", `#${id}`);
}

export function useMediaQuery(query) {
  const get = () => typeof window !== "undefined" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return matches;
}

/** Days/hours/minutes/seconds until `iso`; null once it has passed. */
export function useCountdown(iso) {
  const target = iso ? new Date(iso).getTime() : 0;
  const calc = () => {
    const diff = target - Date.now();
    if (!iso || diff <= 0) return null;
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      minutes: Math.floor((diff / 60000) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [left, setLeft] = useState(calc);
  useEffect(() => {
    if (!iso) return undefined;
    const t = setInterval(() => setLeft(calc()), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iso]);
  return left;
}

/** Tracks which of the given section ids is currently in view. */
export function useActiveSection(ids) {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [ids]);
  return active;
}
